import { Alert, AlertTitle, Button, Card, CardContent, CardHeader, CircularProgress, Container, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { useFormik } from 'formik';
import { useConfirm } from 'material-ui-confirm';
import * as yup from 'yup';
import SelectAdvertiser from '../../../components/SelectAdvertiser';
import SelectCategory from '../../../components/SelectCategory';
import SelectClub from '../../../components/SelectClub';
import SelectOffer from '../../../components/SelectOffer';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';

const MULTICLUBE = 'multiclube';

const schema = yup.object({
    clubId: yup.mixed().required('Selecione o destino'),
    title: yup.string().trim().required('Informe o título'),
    body: yup.string().trim().required('Informe o corpo da notificação'),
});

export default function AdminNotifications() {
    const confirm = useConfirm();
    const { setErrors, enqueueSnackbar } = useErrors();

    const formik = useFormik({
        validationSchema: schema,
        initialValues: { clubId: MULTICLUBE, destType: '', destId: '', title: '', body: '' },
        onSubmit: async values => {
            const isMulticlube = values.clubId === MULTICLUBE;
            try {
                await confirm({
                    title: 'Enviar notificação?',
                    description: isMulticlube
                        ? 'Multiclube: a notificação será enviada sem tópico, para os dispositivos do app Eurobem.'
                        : 'A notificação será enviada no tópico do clube selecionado.',
                    confirmationText: 'Enviar',
                    cancellationText: 'Cancelar',
                });

                const destType = values.destType || undefined;
                const destId = values.destId ? String(values.destId) : undefined;

                const { data } = await server.post('notifications', {
                    clubId: values.clubId,
                    title: values.title.trim(),
                    body: values.body.trim(),
                    destType,
                    destId,
                });

                if (!data?.fn) {
                    throw { response: { data: { message: 'Servidor não confirmou o envio da notificação.' } } };
                }

                formik.resetForm({ values: { clubId: values.clubId, destType: '', destId: '', title: '', body: '' } });
                enqueueSnackbar(
                    data.fn === 'sendNotifyWithoutTopic'
                        ? `Enviada sem tópico (${data.data?.successCount ?? 0} dispositivo(s)).`
                        : `Enviada no tópico ${data.topic}.`,
                    { variant: 'success' },
                );
            } catch (error) {
                if (error === undefined || error?.message === 'cancelled') return;
                setErrors(error);
            }
        },
    });

    const isMulticlube = formik.values.clubId === MULTICLUBE;

    return (
        <Container maxWidth="md">
            <Stack spacing={0.5} pb={2}>
                <Typography variant="h2">Notificações</Typography>
                <Typography color="text.secondary" variant="body2">
                    Multiclube envia sem tópico. Um clube envia no tópico dele. O destino abre oferta, anunciante ou categoria no app.
                </Typography>
            </Stack>

            <Card variant="outlined">
                <CardHeader
                    title="Nova notificação"
                    subheader="Componha a mensagem que será enviada aos assinantes."
                />
                <CardContent>
                    <Stack component="form" onSubmit={formik.handleSubmit} spacing={2}>
                        <SelectClub
                            name="clubId"
                            label="Destino"
                            value={formik.values.clubId}
                            extraOptions={[{ id: MULTICLUBE, label: 'Multiclube' }]}
                            onChange={event => formik.setFieldValue('clubId', event.target.value)}
                        />
                        {formik.touched.clubId && formik.errors.clubId ? (
                            <Typography variant="caption" color="error">
                                {formik.errors.clubId}
                            </Typography>
                        ) : null}

                        <Alert severity="info" icon={<InfoOutlined />}>
                            <AlertTitle>Destino</AlertTitle>
                            {isMulticlube
                                ? 'Multiclube: envio sem tópico, para os dispositivos do aplicativo Eurobem.'
                                : 'Clube: envio no tópico do aplicativo desse clube.'}
                        </Alert>

                        <FormControl fullWidth>
                            <InputLabel>Abrir no app</InputLabel>
                            <Select
                                label="Abrir no app"
                                value={formik.values.destType}
                                onChange={event => {
                                    formik.setFieldValue('destType', event.target.value);
                                    formik.setFieldValue('destId', '');
                                }}
                                disabled={formik.isSubmitting}
                            >
                                <MenuItem value="">Nenhum</MenuItem>
                                <MenuItem value="offer">Oferta</MenuItem>
                                <MenuItem value="advertiser">Anunciante</MenuItem>
                                <MenuItem value="category">Categoria</MenuItem>
                            </Select>
                        </FormControl>

                        {formik.values.destType === 'offer' ? (
                            <SelectOffer
                                label="Oferta"
                                name="destId"
                                value={formik.values.destId}
                                onChange={(_, id) => formik.setFieldValue('destId', id)}
                            />
                        ) : null}
                        {formik.values.destType === 'advertiser' ? (
                            <SelectAdvertiser
                                label="Anunciante"
                                name="destId"
                                value={formik.values.destId}
                                onChange={(_, id) => formik.setFieldValue('destId', id)}
                            />
                        ) : null}
                        {formik.values.destType === 'category' ? (
                            <SelectCategory
                                label="Categoria"
                                name="destId"
                                value={formik.values.destId}
                                onChange={(_, id) => formik.setFieldValue('destId', id)}
                            />
                        ) : null}

                        <TextField
                            label="Título"
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.title && Boolean(formik.errors.title)}
                            helperText={formik.touched.title && formik.errors.title}
                            disabled={formik.isSubmitting}
                            fullWidth
                        />
                        <TextField
                            label="Corpo"
                            name="body"
                            value={formik.values.body}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.body && Boolean(formik.errors.body)}
                            helperText={formik.touched.body && formik.errors.body}
                            disabled={formik.isSubmitting}
                            fullWidth
                            multiline
                            minRows={6}
                        />
                        <Stack direction="row" justifyContent="flex-end">
                            <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
                                {formik.isSubmitting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                Enviar
                            </Button>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Container>
    );
}
